import type { ReactNode } from 'react';
import type { DocPanel } from '../lib/types';

interface Props {
  doc: DocPanel;
  onPrev: () => void;
  onNext: () => void;
  children: ReactNode;
  /** Stretches the panel across the whole workspace. */
  full?: boolean;
}

export function DocumentPanel({ doc, onPrev, onNext, children, full = false }: Props) {
  return (
    <section className={full ? 'docpanel docpanel--full' : 'docpanel'} aria-label={doc.title}>
      <header className="docpanel-head">
        <button type="button" className="pager" onClick={onPrev}>
          <span aria-hidden="true">‹</span> Previous
        </button>
        <h2 className="docpanel-title" title={doc.file}>
          {doc.file}
        </h2>
        <button type="button" className="pager" onClick={onNext}>
          Next <span aria-hidden="true">›</span>
        </button>
      </header>

      <div className="pdfbar" role="toolbar" aria-label="Document viewer controls">
        <ToolIcon extra label="Toggle sidebar" d="M3 3h14v14H3zM7 3v14" />
        <ToolIcon extra label="Find" d="M8.5 3.5a5 5 0 1 0 0 10a5 5 0 1 0 0-10M12.2 12.2 17 17" />
        <span className="pdfbar-sep" />
        <ToolIcon label="Previous page" d="M10 16V4M5 9l5-5 5 5" />
        <ToolIcon label="Next page" d="M10 4v12M5 11l5 5 5-5" />
        <label className="pdfbar-page">
          <span className="pdfbar-page-word">Page:</span>{' '}
          <input type="text" defaultValue={doc.page} size={3} readOnly />
          <span>of {doc.pageCount}</span>
        </label>
        <span className="pdfbar-sep" />
        <ToolIcon label="Zoom out" d="M4 10h12" />
        <ToolIcon label="Zoom in" d="M10 4v12M4 10h12" />
        <select className="pdfbar-zoom" defaultValue="auto" aria-label="Zoom level">
          <option value="auto">Automatic Zoom</option>
          <option value="page-fit">Page Fit</option>
          <option value="page-width">Page Width</option>
          <option value="1">100%</option>
        </select>
        <span className="pdfbar-spacer" />
        <ToolIcon extra label="Presentation mode" d="M3 4h14v9H3zM7 17h6" />
        <ToolIcon extra label="Print" d="M6 8V3h8v5M6 14H4V8h12v6h-2M6 12h8v5H6z" />
        <ToolIcon label="Download" d="M10 3v9M6 9l4 4 4-4M4 16h12" />
        <ToolIcon extra label="Bookmark" d="M6 3h8v14l-4-3.5L6 17z" />
        <ToolIcon extra label="More tools" d="M4 6l4 4-4 4M11 6l4 4-4 4" />
      </div>

      <div className="docpanel-body">{children}</div>
    </section>
  );
}

interface ToolIconProps {
  label: string;
  d: string;
  /** Secondary: dropped from the toolbar on a phone-width screen. */
  extra?: boolean;
}

function ToolIcon({ label, d, extra = false }: ToolIconProps) {
  return (
    <button
      type="button"
      className={extra ? 'tool tool--extra' : 'tool'}
      title={label}
      aria-label={label}
    >
      <svg viewBox="0 0 20 20" width="15" height="15" aria-hidden="true">
        <path
          d={d}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  );
}
